import {
  ChangeDetectorRef, Component, OnInit,
} from '@angular/core';
import { HttpResponse } from '../../../application/types/HttpResponse';
import { EventApiService } from '../../services/event-api/EventApiService';
import {
  EventSeasonType,
  EventStatus,
  SportsEvent,
  Team,
} from '../../types/Event';
import { resolveTeamLogoBackground } from '../../utils/resolveTeamLogoUrl';

type EventSortField = 'date' | 'match' | 'season' | 'tickets' | 'purchase' | 'expenses' | 'status';
type SortDirection = 'asc' | 'desc';

@Component({
  selector: 'app-events-page',
  standalone: false,
  templateUrl: './events-page.component.html',
  styleUrl: './events-page.component.sass',
})
export class EventsPageComponent implements OnInit {
  public events: SportsEvent[] = [];
  public statusFilter: EventStatus | '' = '';
  public page = 1;
  public pageSize = 20;
  public readonly pageSizeOptions = [
    20,
    50,
    100,
  ];
  public loading = false;
  public errorMessage: string | null = null;
  public successMessage: string | null = null;
  public sortField: EventSortField = 'status';
  public sortDirection: SortDirection = 'asc';

  public constructor(
    private eventApi: EventApiService,
    private changeDetector: ChangeDetectorRef,
  ) { }

  public async ngOnInit(): Promise<void> {
    await this.loadEvents();
  }

  public get filteredEvents(): SportsEvent[] {
    return this.statusFilter
      ? this.events.filter((event) => event.status === this.statusFilter)
      : this.events;
  }

  public get sortedEvents(): SportsEvent[] {
    const direction = this.sortDirection === 'asc' ? 1 : -1;

    return [
      ...this.filteredEvents,
    ].sort((left, right) => {
      const defaultCompare = this.compareStatusPriority(left, right)
        || this.compareDate(left, right, 'asc');

      if (this.sortField === 'status') {
        return (this.compareStatusPriority(left, right) || this.compareDate(left, right, 'asc')) * direction;
      }

      const groupedCompare = this.compareStatusPriority(left, right);
      if (groupedCompare !== 0) {
        return groupedCompare;
      }

      return this.compareByField(left, right, this.sortField) * direction || defaultCompare;
    });
  }

  public get pagedEvents(): SportsEvent[] {
    const startIndex = (this.page - 1) * this.pageSize;
    return this.sortedEvents.slice(startIndex, startIndex + this.pageSize);
  }

  public get totalPages(): number {
    return Math.max(Math.ceil(this.filteredEvents.length / this.pageSize), 1);
  }

  public async setStatusFilter(status: EventStatus | ''): Promise<void> {
    this.statusFilter = status;
    this.page = 1;
    await Promise.resolve();
  }

  public goToPage(page: number): void {
    this.page = Math.min(Math.max(page, 1), this.totalPages);
  }

  public setPageSize(pageSize: number): void {
    this.pageSize = pageSize;
    this.page = 1;
  }

  public setSort(field: EventSortField): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      return;
    }

    this.sortField = field;
    this.sortDirection = field === 'status' ? 'asc' : 'desc';
  }

  public sortState(field: EventSortField): string {
    if (this.sortField !== field) {
      return 'none';
    }

    return this.sortDirection === 'asc' ? 'ascending' : 'descending';
  }

  public getSeasonTypeName(type: EventSeasonType): string {
    if (type === 'playoff' || type === 'playoff_stage') {
      return 'Плей-офф';
    }
    if (type === 'cup') {
      return 'Кубок';
    }
    if (type === 'special') {
      return 'Спец-событие';
    }

    return 'Регулярный сезон';
  }

  public getStatusName(status: EventStatus): string {
    if (status === 'completed') {
      return 'Завершен';
    }
    if (status === 'in_progress') {
      return 'В работе';
    }

    return 'Планируется';
  }

  public formatDate(value: string): string {
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(value));
  }

  public formatMoney(value: number): string {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      maximumFractionDigits: 0,
    }).format(value);
  }

  public getLogoBackground(team: Team): string | null {
    return resolveTeamLogoBackground(team.logoUrl);
  }

  public trackById(_index: number, item: { id: string }): string {
    return item.id;
  }

  private async loadEvents(): Promise<void> {
    this.loading = true;
    this.errorMessage = null;

    try {
      const response = await this.eventApi.getEvents();
      const result = this.unwrapResponse(response);
      if (result) {
        this.events = result;
        this.page = Math.min(this.page, this.totalPages);
      }
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.loading = false;
      this.changeDetector.detectChanges();
    }
  }

  private unwrapResponse<ResponseType>(response: HttpResponse<ResponseType>): ResponseType | null {
    if (response.status === 'failed') {
      this.errorMessage = response.errorMessage;
      return null;
    }

    return response.response;
  }

  private handleUnknownError(error: unknown): void {
    this.errorMessage = error instanceof Error ? error.message : 'Не удалось выполнить действие.';
  }

  private compareByField(left: SportsEvent, right: SportsEvent, field: EventSortField): number {
    if (field === 'date') {
      return this.compareDate(left, right, 'asc');
    }
    if (field === 'match') {
      return this.getMatchName(left).localeCompare(this.getMatchName(right), 'ru');
    }
    if (field === 'season') {
      return left.seasonName.localeCompare(right.seasonName, 'ru') || this.compareSeasonType(left, right);
    }
    if (field === 'tickets') {
      return left.summary.ticketsTotal - right.summary.ticketsTotal;
    }
    if (field === 'purchase') {
      return left.summary.purchaseTotal - right.summary.purchaseTotal;
    }
    if (field === 'expenses') {
      return left.summary.expenseTotal - right.summary.expenseTotal;
    }

    return this.compareStatusPriority(left, right);
  }

  private compareStatusPriority(left: SportsEvent, right: SportsEvent): number {
    return this.getStatusPriority(left.status) - this.getStatusPriority(right.status);
  }

  private compareDate(left: SportsEvent, right: SportsEvent, direction: SortDirection): number {
    const diff = new Date(left.startsAt).getTime() - new Date(right.startsAt).getTime();
    return direction === 'asc' ? diff : -diff;
  }

  private compareSeasonType(left: SportsEvent, right: SportsEvent): number {
    return this.getSeasonTypeName(left.seasonType).localeCompare(this.getSeasonTypeName(right.seasonType), 'ru');
  }

  private getMatchName(event: SportsEvent): string {
    return `${event.homeTeam.name} - ${event.awayTeam.name}`;
  }

  private getStatusPriority(status: EventStatus): number {
    if (status === 'in_progress') {
      return 0;
    }
    if (status === 'planned') {
      return 1;
    }

    return 2;
  }
}
